import {
  addListenTime,
  dropStationStats,
  type HistoryEntry,
  type ListenStat,
  parseStats,
  startListenSession,
  statFor,
} from '../domain/history';
import { sameTrack, type Track } from '../domain/icy-parse';
import type { HistoryPort } from '../ports/history';
import type { StoragePort } from '../ports/storage';
import { type AppState, journaledStationId } from './app';
import type { Store } from './index';

/**
 * Dnevnik poslušanja: zabeleži odigrane skladbe (P2 §4) in sešteje čas po
 * postaji (P2 §4.5).
 *
 * Opazuje isti tok stanja kot `nowPlayingWatcher` — prek `sync(state)` iz
 * `render()`. Nič ne teče na uro: čas se šteje ob prehodih (začetek in konec
 * odseka predvajanja), zapis skladbe ob spremembi skladbe. Živi števec za
 * tekočo postajo doda `listenMs()`, ki ga zaslon Zgodovina kliče vsako sekundo
 * iz lastnega intervala.
 */

const VIEW_LIMIT = 1000;

/**
 * Koliko mora postaja mirovati, da isti naslov velja za **nov posluh** (§62).
 *
 * Menjava postaje in ustavitev sta prej sprostili zadnji zapis, zato je obhod
 * A→B→A ali stop→play v nekaj sekundah isto skladbo zabeležil dvakrat. Prag
 * loči obhod od vrnitve; minuta je dovolj dolga za prvo in dovolj kratka, da
 * se pri drugi ne izgubi zapis.
 */
const RELISTEN_MS = 60_000;

export interface JournalDeps {
  store: Store<AppState>;
  history: HistoryPort;
  storage: StoragePort;
  now?: () => number;
  newId?: () => string;
}

export interface Journal {
  /** Prebere shranjen čas in zadnjo zgodovino v store. */
  init(): Promise<void>;
  /** Kliče se ob vsaki spremembi stanja; sam ugotovi, ali je kaj za narediti. */
  sync(state: AppState): void;
  /** Zapiši tekoči odsek v shrambo (ob `pagehide`, preden zavihek izgine). */
  flush(): void;
  /** Skupni čas postaje, vključno z odsekom, ki teče zdaj (za živo uro). */
  listenMs(stationId: string, now: number): number;
  /** Zamenja zgodovino in čas iz varnostne kopije (T2 §7). */
  restore(stats: readonly ListenStat[], history: readonly HistoryEntry[]): Promise<void>;
  clearAll(): Promise<void>;
  clearStation(stationId: string): Promise<void>;
}

function defaultId(): string {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `h-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function createJournal(deps: JournalDeps): Journal {
  const { store, history, storage } = deps;
  const now = deps.now ?? (() => Date.now());
  const newId = deps.newId ?? defaultId;

  /** Postaja, ki ji teče čas; `null`, kadar nič ne igra. */
  let segStation: string | null = null;
  /** Kdaj se je tekoči odsek začel (ali nazadnje zapisal). */
  let segStart = 0;
  /**
   * Zadnja zapisana skladba **po postaji** — da se isti naslov ne beleži
   * dvakrat (§62).
   *
   * `leftAt` je trenutek, ko je postaja nehala igrati; `null` pomeni, da igra
   * zdaj. Zapis se sprosti šele, ko je prekinitev daljša od `RELISTEN_MS` —
   * torej gre za nov posluh, ne za obhod.
   *
   * ⚠️ **Slovar in ne eno mesto**, ker A→B→A ni redkost: z enim mestom bi
   * skladba postaje B prepisala zapis postaje A in ista skladba na A bi se ob
   * vrnitvi zabeležila drugič.
   */
  const lastRecorded = new Map<string, { track: Track; leftAt: number | null }>();

  function persistStats(stats: readonly ListenStat[]): void {
    store.set({ stats });
    void storage.save('stats', stats);
  }

  /** Zapiši pretečeni čas tekočega odseka; premakni izhodišče, da se ne šteje dvakrat. */
  function commitSegment(at: number): void {
    if (segStation === null) return;
    const delta = at - segStart;
    segStart = at;
    if (delta <= 0) return;
    persistStats(addListenTime(store.get().stats, segStation, delta, at));
  }

  function recordEntry(stationId: string, track: Track, artworkUrl: string | null): void {
    const entry: HistoryEntry = {
      id: newId(),
      stationId,
      title: track.title,
      raw: track.raw,
      at: now(),
    };
    if (track.artist !== null && track.artist !== '') entry.artist = track.artist;
    if (artworkUrl !== null) entry.artworkUrl = artworkUrl;

    void history.append(entry);
    store.set({ history: [entry, ...store.get().history].slice(0, VIEW_LIMIT) });
  }

  return {
    async init() {
      const [rawStats, recent] = await Promise.all([
        storage.load<unknown>('stats'),
        history.recent(VIEW_LIMIT),
      ]);
      store.set({ stats: parseStats(rawStats), history: recent });
    },

    sync(state) {
      const at = now();

      // Predposluh iz Iskanja ni poslušanje uporabnikove postaje: v zgodovino
      // in čas ne šteje (isti razlog kot `playCount` v `main.ts`). Merilo je
      // članstvo v seznamu — glej `journaledStationId()`, zakaj ne `isPreviewId`.
      const playing = journaledStationId(state);

      if (playing !== segStation) {
        commitSegment(at);
        // Menjava postaje ali ustavitev zapisa NE sprosti (§62) — zabeleži le,
        // kdaj je postaja utihnila. Sprosti ga šele `RELISTEN_MS` spodaj.
        const leaving = segStation === null ? undefined : lastRecorded.get(segStation);
        if (leaving !== undefined) leaving.leftAt = at;
        segStation = playing;
        segStart = at;
        if (playing !== null) {
          persistStats(startListenSession(store.get().stats, playing, at));
        }
      }

      if (playing === null || state.track === null) return;

      const track = state.track;
      const previous = lastRecorded.get(playing);
      const sameAsLast =
        previous !== undefined &&
        sameTrack(previous.track, track) &&
        (previous.leftAt === null || at - previous.leftAt <= RELISTEN_MS);

      if (sameAsLast) {
        // Postaja spet igra: ura prekinitve se ustavi, sicer bi dolga skladba
        // po minuti predvajanja padla čez prag in se zabeležila drugič.
        previous.leftAt = null;
        return;
      }

      lastRecorded.set(playing, { track, leftAt: null });
      recordEntry(playing, track, state.artworkUrl);
    },

    flush() {
      commitSegment(now());
    },

    listenMs(stationId, at) {
      const committed = statFor(store.get().stats, stationId)?.totalMs ?? 0;
      const live = segStation === stationId ? Math.max(0, at - segStart) : 0;
      return committed + live;
    },

    async restore(nextStats, nextHistory) {
      await history.replaceAll(nextHistory);
      const recent = await history.recent(VIEW_LIMIT);
      // Tekoči odsek pripada zamenjani zgodovini — začni znova, brez podvojitve.
      lastRecorded.clear();
      segStart = now();
      const stats = [...nextStats];
      store.set({ stats, history: recent });
      void storage.save('stats', stats);
    },

    async clearAll() {
      await history.clearAll();
      lastRecorded.clear();
      const at = now();
      segStart = at;
      // Če postaja igra, ji čas teče naprej — počiščen je le do zdaj.
      const stats = segStation === null ? [] : startListenSession([], segStation, at);
      store.set({ history: [], stats });
      void storage.save('stats', stats);
    },

    async clearStation(stationId) {
      await history.clearStation(stationId);
      const at = now();
      let stats = dropStationStats(store.get().stats, stationId);
      lastRecorded.delete(stationId);
      if (segStation === stationId) {
        segStart = at;
        stats = startListenSession(stats, stationId, at);
      }
      store.set({
        history: store.get().history.filter((e) => e.stationId !== stationId),
        stats,
      });
      void storage.save('stats', stats);
    },
  };
}
